import React, {useState, useEffect} from "react"
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Button,
  Select,
  SelectItem,
  Switch,
  Tooltip,
} from "@heroui/react"
import {useCurrentUserData} from "@/utils/hooks/useOrgUsers"

export function EditUserModal({
  isOpen,
  onOpenChange,
  user,
  onSave,
  loading: externalLoading,
  error: externalError,
  initialSensitivity,
  initialRole,
  initialWhitelisted,
  onUpdated,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  user: any
  onSave?: (values: {
    sensitivityLevel: number
    role: string
    isWhitelisted: boolean
  }) => void
  loading?: boolean
  error?: string
  initialSensitivity: number
  initialRole: string
  initialWhitelisted: boolean
  onUpdated?: () => void
}) {
  const {updateUserSettings} = useCurrentUserData()

  const [internalLoading, setInternalLoading] = useState(false)
  const [internalError, setInternalError] = useState<string | undefined>()

  const [editForm, setEditForm] = useState({
    sensitivityLevel: initialSensitivity,
    role: initialRole,
    isWhitelisted: initialWhitelisted,
  })

  useEffect(() => {
    setEditForm({
      sensitivityLevel: initialSensitivity,
      role: initialRole,
      isWhitelisted: initialWhitelisted,
    })
  }, [initialSensitivity, initialRole, initialWhitelisted, user])

  const hasChanges =
    editForm.sensitivityLevel !== initialSensitivity ||
    editForm.role !== initialRole ||
    editForm.isWhitelisted !== initialWhitelisted

  // Decide which loading / error state to display
  const loading = externalLoading ?? internalLoading
  const error = externalError ?? internalError

  const isAdminSelected = editForm.role === "ADMIN"

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      backdrop="transparent"
      size="md"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <p className="text-lg font-medium">Edit User Settings</p>
              <p className="text-sm font-normal">
                Update settings for {user?.full_name}
              </p>
            </ModalHeader>
            <ModalBody className="gap-4">
              {error && <div className="text-sm text-red-600">{error}</div>}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Role</label>
                <Select
                  label="Select Role"
                  selectedKeys={[editForm.role]}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0] as string
                    setEditForm((prev) => ({
                      ...prev,
                      role: key,
                    }))
                  }}
                >
                  <SelectItem key="MEMBER">Member</SelectItem>
                  <SelectItem key="ADMIN">Admin</SelectItem>
                </Select>
              </div>

              {isAdminSelected && (
                <>
                  {/* Sensitive Access Toggle */}
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">Sensitive Read Access</p>
                      <Switch
                        isSelected={editForm.sensitivityLevel === 5}
                        onValueChange={(value) =>
                          setEditForm((prev) => ({
                            ...prev,
                            sensitivityLevel: value ? 5 : 0,
                          }))
                        }
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Should this admin have access to communications marked as
                      sensitive?
                    </p>
                  </div>

                  {/* Communications Archiving Toggle */}
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">
                        Communications archiving required
                      </p>
                      <Switch
                        isSelected={editForm.isWhitelisted}
                        onValueChange={(value) =>
                          setEditForm((prev) => ({
                            ...prev,
                            isWhitelisted: value,
                          }))
                        }
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Does this admin also need their communications monitored?
                    </p>
                  </div>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={async () => {
                  if (onSave) {
                    // Delegate to parent
                    onSave(editForm)
                    return
                  }

                  // Internal save handler
                  if (!user?.auth_user_id) return
                  try {
                    setInternalLoading(true)
                    setInternalError(undefined)

                    await updateUserSettings(user.auth_user_id, {
                      sensitivityReadLevel:
                        editForm.role === "ADMIN"
                          ? editForm.sensitivityLevel
                          : 0,
                      role: editForm.role,
                      isWhitelisted:
                        editForm.role === "ADMIN"
                          ? editForm.isWhitelisted
                          : false,
                    })

                    onUpdated?.()
                    onClose()
                  } catch (e) {
                    setInternalError(
                      e instanceof Error ? e.message : "Failed to update user"
                    )
                  } finally {
                    setInternalLoading(false)
                  }
                }}
                isLoading={loading}
                isDisabled={!hasChanges}
              >
                Save Changes
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
